import React from 'react';
import styles from './lab-binder.module.scss';
import { useLocation } from 'react-router-dom';
import { usePageTitle } from 'src/components/app/use-page-title';
import update from 'immutability-helper';
import { useDrop } from 'react-dnd';
import { LabValueEditor } from '../lab/components/lab-value/lab-value-editor';
import { LabValue } from '../lab/components/lab-value';
import { LabValueDelete } from '../lab/components/lab-value/lab-value-delete';
import { TagsIcons } from '../../../components/tags/tagsIcons';
import {
  Lang,
  INTERNAL_ROUTING,
  ROUTES,
  CATEGORY_TYPES,
  EVENTS,
  ENTITY_TYPES,
  REQUEST_ACCESS_ENTITIES,
} from 'shared/constants';
import { TextTag } from '../../../components/tags/text-tags';
import analyticsService from 'src/helpers/analytics.service';
import { useSelector } from 'react-redux';
import { getUser } from 'shared/store/selectors/user.selector';
import { isCurrentUserValidOwner } from 'shared/store/selectors/owners.selector';
import labServiceApi from 'shared/api/lab.service.api';
import { assertPermission } from 'src/components/common/access-restricted/assert-permission';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import { Button, ContentBinderDrawer } from 'src/components/design-system';
import { Owners } from 'src/components/common/owners';

const LabBinderPageComponent = React.memo(function LabBinderPageComponent({
  id,
  category,
  items = [],
  labSelectValues = () => {},
  initialOpenState = false,
  adminMode = false,
  updateValueOrder = () => {},
  createQuickLabItem = () => {},
  lang = Lang.COMPENDIUM_BUTTONS,
  langPage = Lang.ADMISSION_CHECKLIST_PAGE,
}) {
  const { title, ownerIds, tags, drugClass } = category;
  const { search } = useLocation();

  const { updateSubtitle } = usePageTitle();

  React.useEffect(() => {
    updateSubtitle(title);
  }, [title, updateSubtitle]);

  const [isOpen, setOpenState] = React.useState(initialOpenState);
  const [data, setData] = React.useState(items);

  const [deleteLabValue, setDeleteLabValue] = React.useState(null);
  const [labValueEntity, setLabValueEntity] = React.useState(null);
  const contextId = searchContextIdManager.getContextId();

  React.useEffect(() => {
    if (id) {
      assertPermission({
        serviceMethod: async () => await labServiceApi.getCategoryById({ id }),
        entity: REQUEST_ACCESS_ENTITIES.LAB_CATEGORY,
        id,
      });
    }
  }, [id]);

  const eventData = {
    id,
    title,
    drugClass,
    ...tags,
  };

  React.useEffect(() => {
    if (!id) return;
    analyticsService.track(EVENTS.LAB_ITEMS.OPEN, {
      ...eventData,
      entityType: ENTITY_TYPES.compendium,
      contextId,
    });
    analyticsService.track(EVENTS.CONTENT.OPEN, {
      ...eventData,
      entityType: ENTITY_TYPES.compendium,
      contextId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, contextId]);

  // Drag and Drop
  const dndGroup = `values`;
  const [, drop] = useDrop({ accept: dndGroup });

  const dragItem = (id, atIndex) => {
    const { item, index } = findItem(id);
    setData(
      update(data, {
        $splice: [
          [index, 1],
          [atIndex, 0, item],
        ],
      }),
    );
  };

  const findItem = id => {
    const item = data.find(i => `${i.id}` === id);
    return {
      item,
      index: data.indexOf(item),
    };
  };

  const finishDrag = itemId => {
    updateValueOrder({
      id,
      itemId,
      index: findItem(itemId).index,
    });
  };

  // Delete value items
  const deleteValueAction = React.useCallback(({ id, title }) => {
    // TODO: analytics
    setDeleteLabValue({ id, title });
  }, []);

  const onFinishDelete = React.useCallback(() => {
    setDeleteLabValue(null);
  }, []);

  // Editor
  const onFinishEditor = React.useCallback(() => {
    setLabValueEntity(null);
  }, []);

  const editOrCreateLabValue = React.useCallback(({ item = {} }) => {
    setLabValueEntity({ ...item });
  }, []);

  const createNewLabValue = React.useCallback(
    ({ item = {} }) => {
      setLabValueEntity({ ownerIds, tags, drugClass, ...item });
    },
    [ownerIds, tags, drugClass],
  );

  const currentUser = useSelector(getUser);
  const isUserValidOwner = useSelector(isCurrentUserValidOwner);

  const createQuickValue = React.useCallback(async () => {
    await createQuickLabItem({
      categoryId: category.id,
      ownerIds: isUserValidOwner ? [currentUser.id] : ownerIds,
      tags,
      drugClass,
    });
    analyticsService.track(EVENTS.CONTENT.UPLOAD, {
      entityType: ENTITY_TYPES.compendiumItem,
    });
  }, [createQuickLabItem, category, tags, drugClass, currentUser, ownerIds, isUserValidOwner]);

  const close = React.useCallback(() => {
    labSelectValues(null);
  }, [labSelectValues]);

  React.useEffect(() => {
    if (id) {
      setOpenState(true);
      setData(items);
    } else {
      setOpenState(false);
    }
  }, [id, category, items]);

  React.useEffect(() => {
    const searchId = new URLSearchParams(search).get(INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM);

    if (!searchId && isOpen) {
      setOpenState(false);
    }
  }, [search, isOpen]);

  const renderTags = () => {
    if (!tags?.contentType && !tags?.specialty) return null;

    return (
      <div className={styles.tagsContainer}>
        <TagsIcons tags={tags} hideTags={{ targetAudience: true, contentType: true }} />
        <TextTag tag={tags?.contentType} />
      </div>
    );
  };

  const renderList = () => {
    return data?.map(item => (
      <LabValue
        key={item.id}
        item={item}
        deleteValue={deleteValueAction}
        editValue={editOrCreateLabValue}
        adminMode={adminMode}
        {...{
          dragItem,
          findItem,
          dndGroup,
          finishDrag,
        }}
      />
    ));
  };

  return (
    <>
      <ContentBinderDrawer
        open={isOpen}
        onClose={close}
        title={title}
        shareBoxProps={{
          path: `${ROUTES.LABS}?${INTERNAL_ROUTING.QUERY_PARAMS.COMPENDIUM}=${id}`,
          id,
          title,
          tags,
          entityType: CATEGORY_TYPES.VALUES,
        }}>
        <div className={styles.tagsAndOwnersWrapper}>
          <div className={styles.ownersWrapper}>
            <Owners ownerIds={ownerIds} title={langPage.VALIDATED_BY} />
          </div>
          {renderTags()}
        </div>

        {adminMode && (
          <div className={styles.buttons}>
            <Button
              onClick={createNewLabValue}
              type="primary"
              data-testid="compendium-binder-add-item-button"
              size="middle">
              {lang.ADD}
            </Button>
            <Button onClick={createQuickValue} type="default" size="middle">
              {lang.QUICK_ADD}
            </Button>
          </div>
        )}

        <ul ref={drop}>{renderList()}</ul>
      </ContentBinderDrawer>

      <LabValueDelete open={!!deleteLabValue} entity={deleteLabValue} onFinish={onFinishDelete} />

      <LabValueEditor
        open={!!labValueEntity}
        categoryId={id}
        value={labValueEntity}
        onFinish={onFinishEditor}
      />
    </>
  );
});

export { LabBinderPageComponent };
